/**
 * Issue #138 — sprechender Browsertab.
 *
 * Edge kopiert die Adresse als Link mit dem Seitentitel als Linktext; in Teams/Outlook steht
 * dann genau dieser Titel. Deshalb trägt der Tab auf der Projektseite P-Nummer, Kunde und
 * Projektname, auf allen anderen Seiten den Seitennamen. Die Verdrahtung (Route lesen, Projekt
 * laden, `document.title` setzen) liegt in `components/v2/document-title.tsx`.
 */

export const APP_TITLE = 'Projektverwaltung';

/** Kunde + Projektname werden ab dieser Länge gekürzt; die P-Nummer steht immer vorn. */
export const MAX_PROJECT_LABEL = 60;

export type TabTitleProject = {
  project_key: string;
  customer_name: string;
  name: string;
};

/** „<Seite> – Projektverwaltung", ohne Seite nur „Projektverwaltung". */
export function withAppTitle(label?: string | null): string {
  const trimmed = label?.trim();
  return trimmed ? `${trimmed} – ${APP_TITLE}` : APP_TITLE;
}

/** „P900123 · Kunde · Projektname – Projektverwaltung", lange Titel gekürzt. */
export function projectTabTitle(project: TabTitleProject): string {
  const customer = project.customer_name.trim();
  const name = project.name.trim();
  // Projektnamen enthalten oft schon den Kunden („Kunde – S/4-Einführung"); dann nicht doppelt.
  const label =
    !customer || name.toLocaleLowerCase('de').includes(customer.toLocaleLowerCase('de'))
      ? name
      : `${customer} · ${name}`;
  return withAppTitle(`${project.project_key} · ${truncate(label, MAX_PROJECT_LABEL)}`);
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

export type PageTitle = { key: string; fallback: string };

/**
 * Seitenname je Route, gleiche Übersetzungsschlüssel wie die Sidebar. `null` heißt: kein eigener
 * Seitenname (Projektseite setzt ihren Titel selbst, Deeplink leitet nur weiter).
 */
export function pageTitleFor(pathname: string): PageTitle | null {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  return PAGE_TITLES[first] ?? null;
}

const PAGE_TITLES: Record<string, PageTitle> = {
  '': { key: 'v2.nav.mission_control', fallback: 'Mission Control' },
  atlas: { key: 'v2.nav.atlas', fallback: 'Portfolio Atlas' },
  board: { key: 'v2.nav.board', fallback: 'Phase Board' },
  handover: { key: 'v2.nav.handover', fallback: 'Übergaben' },
  staffing: { key: 'v2.nav.staffing', fallback: 'Staffing' },
  'kfml-worklist': { key: 'v2.nav.kfml_worklist', fallback: 'KFML-Worklist' },
  briefings: { key: 'v2.nav.briefings', fallback: 'Briefings' },
  einstellungen: { key: 'v2.nav.settings', fallback: 'Einstellungen' },
  hilfe: { key: 'hilfe.seite.titel', fallback: 'Hilfe' },
  admin: { key: 'v2.nav.admin', fallback: 'Admin' },
};
